import { fitFontSize } from '../../utils/textFit';
import React, { useState, useEffect, useRef } from 'react';
import { Search, Printer, Users, X, AlertCircle, Settings, Camera, CheckCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import api from '@/api/api';

const CARD_WIDTH = 204;
const CARD_HEIGHT = 324;

const PAPER_SIZES = [
  { id: 'a4', name: 'A4', width: '210mm', height: '297mm', cardsPerPage: 8, columns: 2, description: '8 cards/page (2×4)' },
  { id: 'a3', name: 'A3', width: '297mm', height: '420mm', cardsPerPage: 15, columns: 3, description: '15 cards/page (3×5)' },
  { id: 'cr80', name: 'CR80 (I-Card Machine)', width: '54mm', height: '85.6mm', cardsPerPage: 1, columns: 1, description: '1 card - Standard Size (54×86mm)' }
];

const PrintStaffICard = () => {
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [staffTypeFilter, setStaffTypeFilter] = useState('all');
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [printData, setPrintData] = useState(null);
  const [paperSize, setPaperSize] = useState('a4');
  const [printSide, setPrintSide] = useState('both');
  const [uploadingPhoto, setUploadingPhoto] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [staffRes, templateRes] = await Promise.all([
        api.get('/staff/'),
        api.get('/staff-icard/template')
      ]);
      
      const staffData = staffRes.data.staff || staffRes.data || [];
      setStaffList(staffData);
      setTemplate(templateRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const getStaffPhoto = (staff) => {
    if (staff.photo) return staff.photo;
    if (staff.profile_photo) return staff.profile_photo;
    if (staff.photo_url) return staff.photo_url;
    return null;
  };

  const getStaffType = (staff) => {
    if (staff.staff_type) return staff.staff_type;
    const roles = staff.roles || [];
    if (roles.includes('Teacher') || roles.includes('HOD') || roles.includes('Principal')) {
      return 'Teaching';
    }
    return 'Non-Teaching';
  };

  const handlePhotoUpload = async (staffId, file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload image files only');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo size cannot exceed 5MB');
      return;
    }
    
    setUploadingPhoto(staffId);
    
    try {
      // Resize to max 500px + JPEG compress so the base64 payload stays small
      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();
        const objUrl = URL.createObjectURL(file);
        img.onload = () => {
          const maxDim = 500;
          const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * ratio);
          canvas.height = Math.round(img.height * ratio);
          canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
          URL.revokeObjectURL(objUrl);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        };
        img.onerror = reject;
        img.src = objUrl;
      });

      await api.put(`/staff-icard/${staffId}/photo`, { photo: dataUrl });
      setStaffList(prev => prev.map(s => s.id === staffId ? { ...s, photo: dataUrl } : s));
      toast.success('Photo uploaded successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(null);
    }
  };

  const filteredStaff = staffList.filter(staff => {
    const matchesSearch = 
      (staff.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (staff.employee_id?.toLowerCase() || '').includes(searchTerm.toLowerCase());
    
    const staffType = getStaffType(staff);
    const matchesType = staffTypeFilter === 'all' || staffType === staffTypeFilter;
    
    return matchesSearch && matchesType && !staff.is_blocked && staff.status !== 'Resigned';
  });

  const handleSelectStaff = (staffId) => {
    setSelectedStaff(prev => 
      prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
    );
  };

  const handleSelectAll = () => {
    if (selectedStaff.length === filteredStaff.length) {
      setSelectedStaff([]);
    } else {
      setSelectedStaff(filteredStaff.map(s => s.id));
    }
  };

  const generateICards = async () => {
    if (selectedStaff.length === 0) {
      toast.error('Please select at least one staff');
      return;
    }
    if (!template) {
      toast.error('Please create a template in Staff I-Card Builder first');
      return;
    }

    setGenerating(true);
    try {
      const response = await api.post('/staff-icard/generate-batch', { staff_ids: selectedStaff });
      setPrintData(response.data);
      toast.success(`${response.data.cards.length} Staff I-Cards generated!`);
    } catch (error) {
      toast.error('Failed to generate I-Cards');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = (side = printSide) => {
    const printContent = printRef.current;
    const selectedPaper = PAPER_SIZES.find(p => p.id === paperSize) || PAPER_SIZES[0];
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup blocked! Please allow popups for this site, then click Print again.');
      return;
    }
    
    const cardPairs = printContent.querySelectorAll('.card-pair');
    const isCR80 = paperSize === 'cr80';
    
    let contentHtml = '';
    
    if (isCR80) {
      cardPairs.forEach(pair => {
        if (side === 'front' || side === 'both') {
          const frontCard = pair.querySelector('[data-side="front"] .card-content');
          if (frontCard) contentHtml += `<div class="card-single">${frontCard.outerHTML}</div>`;
        }
        if (side === 'back' || side === 'both') {
          const backCard = pair.querySelector('[data-side="back"] .card-content');
          if (backCard) contentHtml += `<div class="card-single">${backCard.outerHTML}</div>`;
        }
      });
    } else {
      let frontCards = '';
      let backCards = '';
      
      cardPairs.forEach(pair => {
        const frontCard = pair.querySelector('[data-side="front"] .card-content');
        const backCard = pair.querySelector('[data-side="back"] .card-content');
        if (frontCard) frontCards += `<div class="card-item">${frontCard.outerHTML}</div>`;
        if (backCard) backCards += `<div class="card-item">${backCard.outerHTML}</div>`;
      });
      
      if (side === 'front') {
        contentHtml = `<div class="page-grid">${frontCards}</div>`;
      } else if (side === 'back') {
        contentHtml = `<div class="page-grid">${backCards}</div>`;
      } else {
        contentHtml = `<div class="page-grid front-page">${frontCards}</div><div class="page-break"></div><div class="page-grid back-page">${backCards}</div>`;
      }
    }

    const gridColumns = selectedPaper.columns || 2;
    const cardPrintWidth = '54mm';
    const cardPrintHeight = '86mm';
    
    contentHtml = contentHtml.replace(/src="\//g, `src="${window.location.origin}/`);
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Staff I-Cards</title>
          <style>
            @page { size: ${selectedPaper.width} ${selectedPaper.height}; margin: ${isCR80 ? '0' : '5mm'}; }
            * { box-sizing: border-box; }
            body { margin: 0; padding: 0; font-family: system-ui, sans-serif; }
            .card-content { width: ${cardPrintWidth} !important; height: ${cardPrintHeight} !important; overflow: hidden; position: relative; background-size: cover !important; }
            .card-content > div { position: absolute; }
            .card-content img { width: 100%; height: 100%; object-fit: cover; }
            .card-content img.qr-img { object-fit: contain !important; image-rendering: pixelated; background: #fff; }
            .rounded-full { border-radius: 50%; }
            .whitespace-pre-wrap { white-space: pre-wrap; }
            .text-left { text-align: left; }
            .w-full { width: 100%; }
            .bg-gray-200 { background: #e5e7eb; }
            .object-contain { object-fit: contain !important; }
            .overflow-hidden { overflow: hidden; }
            .page-grid { display: grid; grid-template-columns: repeat(${gridColumns}, ${cardPrintWidth}); gap: 5mm; padding: 5mm; justify-content: center; }
            .card-item { page-break-inside: avoid; }
            .page-break { page-break-after: always; height: 0; }
            .card-single { page-break-after: always; display: flex; justify-content: center; align-items: center; }
            .card-single:last-child { page-break-after: auto; }
            .side-label { display: none !important; }
          </style>
        </head>
        <body>${contentHtml}</body>
      </html>
    `);
    printWindow.document.close();
    
    printWindow.onafterprint = async () => {
      try {
        const staffIds = printData.cards.map(c => c.staff.id);
        await api.post('/staff-icard/record-print', { staff_ids: staffIds });
        toast.success(`Print recorded for ${staffIds.length} Staff I-Cards`);
      } catch (error) {
        console.error('Error recording print:', error);
      }
    };
    
    setTimeout(() => { printWindow.focus(); printWindow.print(); setTimeout(() => printWindow.close(), 1500); }, 900);
  };

  const getContactInfo = () => {
    if (!template) return '';
    const parts = [];
    if (template.college_email) parts.push(template.college_email);
    if (template.college_phone) parts.push(template.college_phone);
    return parts.join(' | ');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Printer className="h-7 w-7 text-purple-600" />
            Print Staff I-Card
          </h1>
          <p className="text-gray-600 mt-1">Select staff to print I-Cards</p>
        </div>
        <Link to="/hr/staff-icard-builder" className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg">
          <Settings className="h-4 w-4" />
          Template Settings
        </Link>
      </div>

      {!printData ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b bg-gray-50 flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or Employee ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <select
              value={staffTypeFilter}
              onChange={(e) => setStaffTypeFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
            >
              <option value="all">All Staff Types</option>
              <option value="Teaching">Teaching</option>
              <option value="Non-Teaching">Non-Teaching</option>
            </select>
            <button
              onClick={handleSelectAll}
              className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              {selectedStaff.length === filteredStaff.length && filteredStaff.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {/* Staff List */}
          <div className="max-h-[500px] overflow-y-auto">
            {filteredStaff.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Users className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                <p>No staff found</p>
              </div>
            ) : (
              filteredStaff.map(staff => {
                const staffPhoto = getStaffPhoto(staff);
                const staffType = getStaffType(staff);
                return (
                  <div
                    key={staff.id}
                    className={`flex items-center gap-4 p-4 border-b transition-colors ${
                      selectedStaff.includes(staff.id) ? 'bg-purple-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedStaff.includes(staff.id)}
                      onChange={() => handleSelectStaff(staff.id)}
                      className="w-5 h-5 rounded text-purple-600 focus:ring-purple-500 cursor-pointer"
                    />
                    
                    {/* Photo or Upload */}
                    <div className="relative w-14 h-14 flex-shrink-0">
                      {staffPhoto ? (
                        <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-gray-200">
                          <img src={staffPhoto} className="w-full h-full object-cover" alt="" />
                        </div>
                      ) : (
                        <label className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-400 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-100 hover:border-purple-500 transition-colors">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handlePhotoUpload(staff.id, e.target.files[0])}
                            disabled={uploadingPhoto === staff.id}
                          />
                          {uploadingPhoto === staff.id ? (
                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
                          ) : (
                            <>
                              <Camera className="w-5 h-5 text-gray-400" />
                              <span className="text-[8px] text-gray-500 mt-0.5">Upload</span>
                            </>
                          )}
                        </label>
                      )}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900">{staff.full_name}</p>
                      <p className="text-sm text-gray-500">
                        {staff.employee_id} • {staff.designation} • {staff.department}
                      </p>
                      {!staffPhoto && (
                        <p className="text-xs text-orange-600 mt-1">⚠️ No photo - Upload required</p>
                      )}
                    </div>
                    
                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                      staffType === 'Teaching' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                    }`}>
                      {staffType}
                    </span>
                    
                    {staff.icard_print_count > 0 && (
                      <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                        Printed: {staff.icard_print_count}x
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
            <p className="text-sm text-gray-600">
              {selectedStaff.length} Staff selected
            </p>
            <button
              onClick={generateICards}
              disabled={selectedStaff.length === 0 || generating}
              className="flex items-center gap-2 px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {generating ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <Printer className="h-4 w-4" />
              )}
              Generate I-Cards
            </button>
          </div>
        </div>
      ) : (
        /* Print Preview */
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <div className="p-4 border-b bg-green-50">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-green-800">✅ {printData.cards.length} Staff I-Cards Generated!</h2>
                <p className="text-sm text-green-600">Use the buttons below to print</p>
              </div>
              <button onClick={() => setPrintData(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">← Back</button>
            </div>
            
            <div className="flex flex-wrap items-center gap-4 p-4 bg-white rounded-lg border">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Paper Size:</label>
                <select value={paperSize} onChange={(e) => setPaperSize(e.target.value)} className="px-3 py-1.5 border rounded-lg text-sm">
                  {PAPER_SIZES.map(size => (<option key={size.id} value={size.id}>{size.name} - {size.description}</option>))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-gray-700">Print:</label>
                <div className="flex gap-1">
                  <button onClick={() => setPrintSide('front')} className={`px-3 py-1.5 text-sm rounded-lg border ${printSide === 'front' ? 'bg-purple-600 text-white' : 'bg-white'}`}>Front Only</button>
                  <button onClick={() => setPrintSide('back')} className={`px-3 py-1.5 text-sm rounded-lg border ${printSide === 'back' ? 'bg-purple-600 text-white' : 'bg-white'}`}>Back Only</button>
                  <button onClick={() => setPrintSide('both')} className={`px-3 py-1.5 text-sm rounded-lg border ${printSide === 'both' ? 'bg-purple-600 text-white' : 'bg-white'}`}>Both (Duplex)</button>
                </div>
              </div>
              
              <button onClick={() => handlePrint()} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium ml-auto">
                <Printer className="h-4 w-4" />
                Print {printSide === 'front' ? 'Front Only' : printSide === 'back' ? 'Back Only' : '(Front+Back)'}
              </button>
            </div>
            
            {printSide === 'both' && paperSize !== 'cr80' && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg text-sm text-purple-800">
                <strong>💡 Duplex Print Tip:</strong> Page 1 contains all Front sides and Page 2 contains all Back sides. Select "Flip on Long Edge" option in your printer settings.
              </div>
            )}
          </div>

          {/* Cards Preview */}
          <div className="p-6 bg-gray-100 overflow-auto max-h-[70vh]" ref={printRef}>
            <div className="space-y-8">
              {printData.cards.map((card, index) => (
                <div key={index} className="card-pair flex gap-6 justify-center bg-white p-6 rounded-lg shadow-md">
                  {/* Front Side */}
                  <div data-side="front" className="card-wrapper">
                    <p className="text-center text-xs text-gray-500 mb-2 side-label">Front Side</p>
                    <div 
                      className="card-content relative bg-white shadow-lg"
                      style={{ width: `${CARD_WIDTH}px`, height: `${CARD_HEIGHT}px`, backgroundImage: template?.front_background ? `url(${template.front_background})` : 'none', backgroundSize: 'cover', border: '1px solid #e5e7eb' }}
                    >
                      {template?.front_fields?.filter(f => f.visible).map(field => {
                        const isRound = field.shape === 'round';
                        const isMedia = ['photo', 'logo', 'qr_code', 'qr', 'signature'].includes(field.id);
                        return (
                          <div key={field.id} className={`absolute ${isMedia ? 'overflow-hidden' : ''}`} style={{ left: `${field.x}px`, top: `${field.y}px`, width: `${field.width}px`, height: isMedia ? `${field.height}px` : 'auto', minHeight: `${field.height}px`, fontSize: (field.id === 'college_name' || field.id === 'college_address') ? `${fitFontSize(field.id === 'college_name' ? template?.college_name : template?.college_address, field.fontSize, field.width, field.height)}px` : `${field.fontSize}px`, color: field.color, fontWeight: field.fontWeight, textAlign: field.textAlign, display: 'flex', alignItems: 'center', justifyContent: field.textAlign === 'center' ? 'center' : 'flex-start', lineHeight: 1.15, borderRadius: isRound ? '50%' : '0' }}>
                            {field.id === 'photo' && (card.staff.photo ? <img src={card.staff.photo} className={`w-full h-full object-cover ${isRound ? 'rounded-full' : ''}`} alt="" /> : <div className={`w-full h-full bg-gray-200 flex items-center justify-center ${isRound ? 'rounded-full' : ''}`}><Users className="w-1/2 h-1/2 text-gray-400" /></div>)}
                            {field.id === 'logo' && template?.college_logo && <img src={template.college_logo} className="w-full h-full object-contain" alt="" />}
                            {field.id === 'college_name' && template?.college_name}
                            {field.id === 'college_address' && template?.college_address}
                            {field.id === 'name' && card.staff.name}
                            {field.id === 'staff_type' && card.staff.staff_type}
                            {field.id === 'designation' && card.staff.designation}
                            {field.id === 'department' && card.staff.department}
                            {field.id === 'employee_id' && `ID: ${card.staff.employee_id}`}
                            {field.id === 'join_date' && `Join: ${card.staff.join_date}`}
                            {field.id === 'phone' && `Ph: ${card.staff.phone}`}
                            {field.id === 'blood_group' && card.staff.blood_group && `Blood: ${card.staff.blood_group}`}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Back Side */}
                  <div data-side="back" className="card-wrapper">
                    <p className="text-center text-xs text-gray-500 mb-2 side-label">Back Side</p>
                    <div 
                      className="card-content relative bg-white shadow-lg"
                      style={{ width: `${CARD_WIDTH}px`, height: `${CARD_HEIGHT}px`, backgroundImage: template?.back_background ? `url(${template.back_background})` : 'none', backgroundSize: 'cover', border: '1px solid #e5e7eb' }}
                    >
                      {template?.back_fields?.filter(f => f.visible).map(field => {
                        const isMedia = ['photo', 'logo', 'qr_code', 'qr', 'signature'].includes(field.id);
                        const isTextarea = field.type === 'textarea';
                        return (
                        <div key={field.id} className={`absolute ${(isMedia || isTextarea) ? 'overflow-hidden' : ''}`} style={{ left: `${field.x}px`, top: `${field.y}px`, width: `${field.width}px`, height: (isMedia || isTextarea) ? `${field.height}px` : 'auto', minHeight: `${field.height}px`, fontSize: `${field.fontSize}px`, color: field.color, fontWeight: field.fontWeight, textAlign: field.textAlign, display: 'flex', alignItems: isTextarea ? 'flex-start' : 'center', justifyContent: field.textAlign === 'center' ? 'center' : 'flex-start', lineHeight: isTextarea ? '1.4' : 1.15 }}>
                          {field.id === 'terms_title' && template?.terms_title}
                          {field.id === 'terms' && <div className="whitespace-pre-wrap text-left w-full">{template?.terms_text}</div>}
                          {field.id === 'employee_id_back' && `Employee ID: ${card.staff.employee_id}`}
                          {field.id === 'qr_code' && card.qr_code && <img src={card.qr_code} className="w-full h-full qr-img" style={{ objectFit: 'contain', imageRendering: 'pixelated', background: '#fff' }} alt="QR" />}
                          {field.id === 'college_contact' && getContactInfo()}
                        </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrintStaffICard;
